import { useAppStore } from '@/app/stores/app.store.js';

const OVERLAY_LAYOUT = 'overlay';

/**
 * A router guard that tracks the previous route visited by the user.
 * It saves the previous route information in the app store for later use.
 *
 * Behaviour:
 * - When navigating TO an overlay from a non-overlay route, saves the non-overlay route
 * - When navigating AWAY from an overlay, clears the saved route
 * - When navigating between overlays, keeps the original non-overlay route (does not update)
 * - On initial page load to an overlay, does nothing (prevents saving invalid route)
 *
 * @param {import('vue-router').RouteLocationNormalized} to - Target route
 * @param {import('vue-router').RouteLocationNormalized} from - Current route
 */
export function trackPreviousRoute(to, from) {
    const appStore = useAppStore();
    const isInitialNavigation = !from.name;
    const isToOverlay = to.meta?.layout === OVERLAY_LAYOUT;
    const isFromOverlay = from.meta?.layout === OVERLAY_LAYOUT;

    // Skip processing on initial page load to an overlay
    // Handles both refresh (preserves existing session storage) and fresh tab (prevents invalid save)
    if (isInitialNavigation && isToOverlay) {
        return;
    }

    // Clear the previous route when navigating away from an overlay
    if (!isToOverlay) {
        appStore.setPreviousRoute(null);
        return;
    }

    // Save the previous route when entering an overlay from a non-overlay route
    // Note: When navigating between overlays, we intentionally keep the original
    // non-overlay route so closing returns to the pre-overlay page
    if (isToOverlay && !isFromOverlay) {
        appStore.setPreviousRoute(from.fullPath);
    }
}
